import type { UseQueryResult } from "react-query";
import { useQuery } from "react-query";

import apiBase from "../../utils/apiBase";

export interface Message {
  role: string;
  content: string;
  toolCalls?: string;
}
interface RequestItem {
  id: string;
  organizationId: string;
  applicationId: string;
  cacheItemId: string;
  cacheType: string;
  request: {
    path: string;
    user: {
      id: string;
      encodedIp: string;
    };
    serverIp: string;
    timestamp: string;
    headers: Record<string, string>;
    body: {
      messages: Message[];
      stream: boolean;
      maxTokens: number;
    };
  };
  response: {
    statusCode: number;
    model: string;
    totalTime: number;
    totalTokens: number;
    completionTokens: number;
    promptTokens: number;
    similarityScore: number;
    similarityThreshold: number;
    headers: Record<string, string>;
    body: {
      object: string;
      created: number;
      model: string;
      choices: {
        message: Message;
      }[];
    };
  };
}

export interface IRequestsRequest {
  PageIndex: number | null;
  PageSize?: number;
  StatusCode?: number | null;
  user?: string;
  Search?: string;
  ApplicationId?: string;
  dateFilter?: null | {
    fromDate: Date;
    toDate?: Date;
  };
}

interface IRequestsResponse {
  totalCount: number;
  filteredCount: number;
  pageIndex: number;
  pageSize: number;
  search: string;
  items: RequestItem[];
}

export const fetchRequests = async (
  organizationId: string,
  {
    PageIndex,
    user,
    Search,
    ApplicationId,
    StatusCode,
    dateFilter,
  }: IRequestsRequest,
): Promise<IRequestsResponse> => {
  const getParams: any = {};

  if (PageIndex) {
    getParams.pageIndex = PageIndex - 1;
  }

  if (user) {
    getParams.user = user;
  }

  if (Search) {
    getParams.search = Search;
  }

  if (StatusCode) {
    getParams.statusCode = StatusCode;
  }

  if (ApplicationId) {
    getParams.applicationId = ApplicationId;
  }

  if (dateFilter) {
    getParams.fromDate = dateFilter.fromDate;
    getParams.toDate = dateFilter.toDate;
  }

  const response: IRequestsResponse = await apiBase.get(
    `/organizations/${organizationId}/requests`,
    {
      params: getParams,
    },
  );

  return response;
};

export const exportRequests = async (
  organizationId: string,
): Promise<string> => {
  return await apiBase.get(`/organizations/${organizationId}/requests/export`);
};

export const useRequests = (
  organizationId: string,
  request: IRequestsRequest,
): UseQueryResult<IRequestsResponse, unknown> => {
  return useQuery(
    [
      "Requests",
      organizationId,
      request.PageIndex,
      request.user,
      request.Search,
      request.ApplicationId,
      request.StatusCode,
      request.dateFilter,
    ],
    () => fetchRequests(organizationId, request),
    {
      enabled: !!organizationId,
    },
  );
};

export const useExportRequests = (organizationId: string) => {
  return useQuery([], () => exportRequests(organizationId), {
    enabled: false,
  });
};
